import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import { Alert, Button } from "@snapchat/snap-design-system";

import { PageContent, PageContentSpinner } from "../../layout/components";
import { Flexbox } from "../../shared/components";
import {
    type CreatedOrganization,
    useCreateOrganizationAndLensSpace,
    useDefaultOrganizationAndLensSpaceNames,
} from "../../shared/hooks";
import {
    LensSpaceTextInputSection,
    LensSubmissionFlowPageHeader,
    OrganizationTextInputSection,
    createLensSpaceButton,
    createTextInputHandler,
    pageContent,
    sectionMargin,
} from "../../submit/components/lens-space";
import { isInvalidTextInput } from "../../submit/validation";

import {
    CreateLensFolder,
    CreateOrganizationAndLensFolderHeader,
    CreateOrganizationAndLensFolderSubHeader,
    CreateOrganizationFailed,
} from "./messages.generated";

interface CreateOrganizationAndLensSpacePage {
    defaultOrganizationName: string;
    defaultLensSpaceName: string;
    onCreationSuccess: (organization: CreatedOrganization) => void;
}

const CreateOrganizationAndLensSpacePage: FC<CreateOrganizationAndLensSpacePage> = ({
    defaultOrganizationName,
    defaultLensSpaceName,
    onCreationSuccess,
}) => {
    const [organizationName, setOrganizationName] = useState(defaultOrganizationName);
    const [lensSpaceName, setLensSpaceName] = useState(defaultLensSpaceName);
    const {
        createOrganizationAndLensSpace,
        data: createdOrganization,
        error: creationError,
        loading: isCreatingOrganizationAndLensSpace,
    } = useCreateOrganizationAndLensSpace(organizationName, lensSpaceName);
    // The create command is void by design, so hand over once the org exists and its refetch has
    // settled — even if the refetch failed: the org can't be created twice from here.
    useEffect(() => {
        if (createdOrganization && !isCreatingOrganizationAndLensSpace) {
            onCreationSuccess(createdOrganization);
        }
    }, [createdOrganization, isCreatingOrganizationAndLensSpace, onCreationSuccess]);
    const isOrganizationNameInvalid = isInvalidTextInput(organizationName);
    const isLensSpaceNameInvalid = isInvalidTextInput(lensSpaceName);
    const isCreateLensFolderButtonDisabled =
        isCreatingOrganizationAndLensSpace ||
        Boolean(createdOrganization) ||
        isOrganizationNameInvalid ||
        isLensSpaceNameInvalid;
    const handleOrganizationNameChange = useMemo(
        () => createTextInputHandler(setOrganizationName),
        [setOrganizationName],
    );
    const handleLensSpaceNameChange = useMemo(() => createTextInputHandler(setLensSpaceName), [setLensSpaceName]);
    const handleCreate = useCallback(() => {
        createOrganizationAndLensSpace();
    }, [createOrganizationAndLensSpace]);

    return (
        <PageContent>
            <LensSubmissionFlowPageHeader
                headerText={<CreateOrganizationAndLensFolderHeader />}
                subheaderText={<CreateOrganizationAndLensFolderSubHeader />}
            />
            <Flexbox flexDirection="column" className={pageContent}>
                <div>
                    <OrganizationTextInputSection
                        inputValue={organizationName}
                        onChange={handleOrganizationNameChange}
                        isLoading={isCreatingOrganizationAndLensSpace}
                    />
                    <LensSpaceTextInputSection
                        inputValue={lensSpaceName}
                        onChange={handleLensSpaceNameChange}
                        isLoading={isCreatingOrganizationAndLensSpace}
                    />
                    {creationError && !createdOrganization ? (
                        <div className={sectionMargin}>
                            <Alert status="error" title={<CreateOrganizationFailed />} />
                        </div>
                    ) : null}
                </div>
                <Flexbox flexDirection="row-reverse">
                    <Button
                        type="primary"
                        className={createLensSpaceButton}
                        isDisabled={isCreateLensFolderButtonDisabled}
                        onClick={handleCreate}
                        isPending={isCreatingOrganizationAndLensSpace}
                    >
                        <CreateLensFolder />
                    </Button>
                </Flexbox>
            </Flexbox>
        </PageContent>
    );
};

interface CreateOrganizationAndLensSpacePageContainer {
    onCreationSuccess: (organization: CreatedOrganization) => void;
    forceUseDefaultNames?: boolean;
}

export const CreateOrganizationAndLensSpacePageContainer: FC<CreateOrganizationAndLensSpacePageContainer> = ({
    onCreationSuccess,
    forceUseDefaultNames,
}) => {
    const { loading, defaultLensSpaceName, defaultOrganizationName } =
        useDefaultOrganizationAndLensSpaceNames(forceUseDefaultNames);
    if (loading) {
        return <PageContentSpinner />;
    }
    return (
        <CreateOrganizationAndLensSpacePage
            defaultLensSpaceName={defaultLensSpaceName}
            defaultOrganizationName={defaultOrganizationName}
            onCreationSuccess={onCreationSuccess}
        />
    );
};
